import {
  Capacitor,
  registerPlugin,
  PluginListenerHandle,
} from "@capacitor/core";

/**
 * Native side of the "auto close / auto reopen" feature (Android only).
 *
 * The timing itself ALWAYS lives in JavaScript: every trigger is an absolute epoch
 * millisecond timestamp (`Date.now()` based) handed to the plugin. Native only makes
 * sure the moment is honoured even when the screen is off, the device is locked, the
 * system is in Doze / battery saving mode or the app process is already dead.
 *
 * Android only — guard calls with {@link isAppLifecycleAvailable} so the web / iOS
 * builds keep working.
 */
export type AppLifecycleTrigger = "close" | "reopen" | "reopen_transient";

/** "exact" = setExactAndAllowWhileIdle (Doze proof, cheapest, default).
 *  "alarmClock" = setAlarmClock (strongest guarantee + wakes the device, more battery). */
export type AppLifecycleReopenMode = "exact" | "alarmClock";

export interface AppLifecycleSchedule {
  /** Epoch ms of the armed auto close, null when nothing is scheduled. */
  closeAt: number | null;
  /** Epoch ms of the armed auto reopen, null when nothing is scheduled. */
  reopenAt: number | null;
  reopenMode: AppLifecycleReopenMode;
  wakeScreen: boolean;
  closeGraceMs: number;
  recoverMissedReopen: boolean;
  missedGraceMs: number;
  closeArmed: boolean;
  reopenArmed: boolean;
  /** Last trigger that fired on this device ("close" | "reopen" | "reopen_transient" | ""). */
  lastTrigger: string;
  lastTriggerAt: number;
  lastTriggerRecovered: boolean;
  appRunning: boolean;
  /** Present when a provided time was rejected (already in the past). */
  warnings?: {
    closeAtInPast?: boolean;
    reopenAtInPast?: boolean;
  };
}

export interface AppLifecycleSetScheduleOptions {
  /** Epoch ms when the app has to be closed (null / 0 clears the trigger). */
  closeAt?: number | null;
  /** Epoch ms when the app has to be (re)opened (null / 0 clears the trigger). */
  reopenAt?: number | null;
  reopenMode?: AppLifecycleReopenMode;
  /** Turn the screen on / show the app over the keyguard when reopening. */
  wakeScreen?: boolean;
  /** Time given to JavaScript to persist state before the process is killed. */
  closeGraceMs?: number;
  /** Reopen on boot when the reopen moment was missed while the device was off. */
  recoverMissedReopen?: boolean;
  /** Optional upper bound (ms) on how late a missed reopen may still be recovered. */
  missedGraceMs?: number;
  /** Full screen intent notification texts (shown only when wakeScreen is enabled). */
  notification?: {
    title?: string;
    body?: string;
    /** true (default) = no sound / vibration. */
    silent?: boolean;
  };
}

export interface AppLifecycleCapabilities {
  exactAlarmsAllowed: boolean;
  fullScreenIntentAllowed: boolean;
  notificationsAllowed: boolean;
  notificationPermission:
    | "granted"
    | "denied"
    | "prompt"
    | "prompt-with-rationale";
  overlayAllowed: boolean;
  ignoringBatteryOptimizations: boolean;
  appRunning: boolean;
  sdkInt: number;
  /** exact alarms armed AND (full screen intent OR overlay) available. */
  backgroundLaunchGuaranteed: boolean;
  /** Exact alarms had to fall back to an inexact (but still Doze proof) alarm. */
  exactAlarmDegraded: boolean;
}

export interface AppLifecycleLaunchInfo {
  /** true only when THIS app session was started by the schedule. */
  autoLaunched: boolean;
  trigger: AppLifecycleTrigger | null;
  at: number | null;
  recovered: boolean;
  wakeScreen: boolean;
}

export interface AppLifecyclePermissionResult {
  allowed: boolean;
  /** true when a system settings screen was opened and the user has to act. */
  opened?: boolean;
}

export interface AppLifecyclePlugin {
  setSchedule(
    options: AppLifecycleSetScheduleOptions,
  ): Promise<AppLifecycleSchedule>;
  getSchedule(): Promise<AppLifecycleSchedule>;
  clearSchedule(): Promise<AppLifecycleSchedule>;

  /** Closes the app now (an already armed reopen trigger survives). */
  closeApp(options?: {
    graceMs?: number;
  }): Promise<{ closing: boolean; graceMs: number }>;
  /** Kills the process and relaunches the app after delayMs (default 1200ms). */
  restartApp(options?: {
    delayMs?: number;
    mode?: AppLifecycleReopenMode;
    wakeScreen?: boolean;
  }): Promise<{
    restarting: boolean;
    delayMs: number;
    mode: AppLifecycleReopenMode;
  }>;

  getLaunchInfo(): Promise<AppLifecycleLaunchInfo>;
  capabilities(): Promise<AppLifecycleCapabilities>;

  checkNotificationPermission(): Promise<{ granted: boolean; state: string }>;
  requestNotificationPermission(): Promise<{ granted: boolean; state: string }>;

  canScheduleExactAlarms(): Promise<{ allowed: boolean }>;
  requestExactAlarmPermission(): Promise<AppLifecyclePermissionResult>;
  canUseFullScreenIntent(): Promise<{ allowed: boolean }>;
  requestFullScreenIntentPermission(): Promise<AppLifecyclePermissionResult>;
  canDrawOverlays(): Promise<{ allowed: boolean }>;
  requestOverlayPermission(): Promise<AppLifecyclePermissionResult>;
  isIgnoringBatteryOptimizations(): Promise<{ allowed: boolean }>;
  requestIgnoreBatteryOptimizations(options?: {
    appDetails?: boolean;
  }): Promise<{ opened: boolean }>;
  /** OEM "auto start" / "protected apps" screen (Xiaomi, Oppo, Vivo, Huawei, ...). */
  requestAutoStartPermission(): Promise<{ opened: boolean; target?: string }>;

  /** Fired when the schedule (re)opened the app. Retained until a listener is added. */
  addListener(
    eventName: "autoLaunched",
    listener: (data: {
      trigger: AppLifecycleTrigger;
      at: number;
      recovered: boolean;
      wakeScreen: boolean;
    }) => void,
  ): Promise<PluginListenerHandle>;

  /** Fired right before the scheduled close, during the configured grace period. */
  addListener(
    eventName: "aboutToClose",
    listener: (data: {
      trigger: AppLifecycleTrigger;
      at: number;
      graceMs: number;
    }) => void,
  ): Promise<PluginListenerHandle>;
}

export const AppLifecycle = registerPlugin<AppLifecyclePlugin>("AppLifecycle");

/** False on web and iOS — always check before calling the plugin. */
export const isAppLifecycleAvailable = (): boolean =>
  Capacitor.isNativePlatform() && Capacitor.isPluginAvailable("AppLifecycle");

export default AppLifecycle;
